import { Navigate } from "react-router-dom";
import { useState } from "react";
import { BadgeCheck, Check, Copy, LogOut, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";

export default function AccountPage() {
  const { user, logout } = useAuth();
  const [copied, setCopied] = useState(false);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F5F2]">
        <p className="eyebrow" data-testid="account-loading">
          Loading
        </p>
      </div>
    );
  }
  if (user === false) {
    return <Navigate to="/" replace />;
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText("WELCOME10");
      setCopied(true);
      toast.success("Code copied — see you at checkout.");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Could not copy — the code is WELCOME10.");
    }
  };

  return (
    <div data-testid="account-page" className="min-h-screen bg-[#F8F5F2] pt-32 md:pt-40 pb-24 px-6">
      <div className="mx-auto max-w-[720px]">
        <Reveal>
          <p className="eyebrow">Vivoré Member</p>
          <h1 className="font-display text-4xl md:text-5xl mt-3 text-ink">Your account</h1>
          <p className="mt-3 font-light text-sm text-stone" data-testid="account-email">
            Signed in as {user.email}
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            data-testid="member-offer-card"
            className="mt-10 bg-[#FDFCF9] border border-ink/8 rounded-[26px] p-8 md:p-10"
          >
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-lavsoft flex items-center justify-center shrink-0">
                <BadgeCheck size={17} strokeWidth={1.6} className="text-lavdeep" />
              </span>
              <h2 className="font-display text-2xl md:text-3xl text-ink">
                10% off your first order
              </h2>
            </div>
            <p className="mt-4 font-light text-sm text-stone leading-relaxed">
              Thank you for joining the VIVORÉ list. Enter this code on the secure
              Shopify checkout after you press Checkout:
            </p>
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <span
                data-testid="account-code"
                className="font-sans tracking-[0.2em] text-lg bg-lavsoft text-lavdeep px-6 py-3 rounded-xl border border-lavdeep/20"
              >
                WELCOME10
              </span>
              <button
                data-testid="copy-code-button"
                onClick={copy}
                className="font-sans text-[11px] uppercase tracking-widest2 text-ink border border-ink/30 rounded-full px-5 py-3 hover:bg-ink hover:text-ivory transition-all duration-500 flex items-center gap-2"
              >
                {copied ? <Check size={13} strokeWidth={2} /> : <Copy size={13} strokeWidth={1.5} />}
                {copied ? "Copied" : "Copy code"}
              </button>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-8 flex items-center gap-4 flex-wrap">
            <button
              data-testid="account-shop-button"
              onClick={() => (window.location.href = "/products/luxe-hydrating-cream")}
              className="btn-lux bg-ink text-ivory text-xs uppercase tracking-widest2 px-9 py-4 rounded-full flex items-center gap-2.5"
            >
              Shop the Luxe Hydrating Cream <ShoppingBag size={14} strokeWidth={1.5} />
            </button>
            <button
              data-testid="account-signout-button"
              onClick={async () => {
                await logout();
                toast.success("Signed out — see you soon.");
              }}
              className="font-sans text-[11px] uppercase tracking-widest2 text-stone hover:text-ink transition-colors duration-500 flex items-center gap-2"
            >
              <LogOut size={13} strokeWidth={1.5} /> Sign out
            </button>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
